import React, { useEffect, useState } from "react";
import TextField from "components/Form/TextField";
import {
  Checkbox,
  InputAdornment,
  Typography,
} from "@mui/material";
import CircleCheckedFilled from '@material-ui/icons/CheckCircle';
import  FilledCircleIcon from 'components/Common'
import { Form, Formik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import RichEditor from "components/RichEditor";
import { error } from "config/colors";
import { v4 as uuidv4 } from "uuid";
import IconButton from "@mui/material/IconButton";
import { Delete } from "@mui/icons-material";
import Button from "components/button/Button";
import ReactQuill from "react-quill";

export interface Question {
  description: string;
  options: FormOption[];
  score: number;
}

interface FormOption {
  description: string;
  uid: string | number;
  is_answer?: boolean;
}

interface IAddQuestionFormProps {
  onSubmit: (question: Question) => Promise<void | any>;
  initialQuestion?: Question;
  inEditMode?: boolean;
  autoClearFormOnSubmit?: boolean;
}

const validationSchema = Yup.object().shape({
  points: Yup.number()
    .min(1, "Enter positive score")
    .required("Enter attainable score for this question"),
});

const makeFormOptions = (count: number) => {
  const options: FormOption[] = [];

  for (let i = 0; i < count; i++) {
    options.push({
      description: "",
      uid: uuidv4(),
    });
  }
  return options;
};

const AddQuestionForm = ({
  onSubmit,
  initialQuestion,
  inEditMode,
  autoClearFormOnSubmit,
}: IAddQuestionFormProps) => {
  const [options, setOptions] = useState<FormOption[]>(makeFormOptions(2));
  const [selectedOptionId, setSelectedOptionId] = useState<string | number>("");
  const [questionDescription, setQuestionDescription] = useState<string>("");
  const [formErrorMessage, setFormErrorMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialQuestion) {
      //update question description from initial question prop.
      setQuestionDescription(initialQuestion?.description || "");

      const optionsToUse = initialQuestion?.options || makeFormOptions(2);
      setOptions(optionsToUse);

      const selectedOptionId = optionsToUse.find(
        (option) => option.is_answer
      )?.uid;
      setSelectedOptionId(selectedOptionId || "");
    }
  }, [initialQuestion]);

  const updateOption = (uid: string | number, description: string) => {
    const newOptions = [...options].map((option) => {
      if (option.uid === uid) {
        return { uid, description } as FormOption;
      }
      return option;
    });
    setOptions(newOptions);
  };

  const addToOptions = () => {
    const newOptions = [...options, ...makeFormOptions(1)];
    setOptions(newOptions);
  };

  const deleteOption = (uid: string | number) => {
    const newOptions = [...options].filter((option) => option.uid !== uid);
    setOptions(newOptions);
  };

  const validateFormData = () => {
    let error = "";
    if (!questionDescription || questionDescription.length < 12) {
      error =
        "Make sure the question description field is filled with not less than 12 characters";
    } else if (options.some((option) => !option.description)) {
      error = "Empty option texts aren't allowed";
    } else if (!options.some((option) => option.uid === selectedOptionId)) {
      error = "Please select the option that correctly answers the question";
    }

    if (error) {
      setFormErrorMessage(error);
      return false;
    } else {
      setFormErrorMessage("");
    }
    return true;
  };

  const formatAndSubmit = async (values: any) => {
    const formatedValues = {
      score: values.points,
      description: questionDescription,
      options: options.map((option) => {
        return { ...option, is_answer: option.uid === selectedOptionId };
      }),
    };

    if (validateFormData()) {
      await onSubmit(formatedValues);
      if (autoClearFormOnSubmit) {
        setQuestionDescription("");
        setOptions(makeFormOptions(2));
      }
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize
      initialValues={{
        points: initialQuestion?.score || 1,
      }}
      onSubmit={async (values: any, { resetForm }) => {
        setIsSubmitting(true);
        formatAndSubmit(values).finally(() => {
          setIsSubmitting(false);
          if (autoClearFormOnSubmit) {
            resetForm();
          }
        });
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => {
        return (
          <Form style={{ textAlign: "center" }}>
            <div style={{ padding: 16 }}>
              <TextFieldContainer
                direction="column"
                style={{ alignItems: "baseline", marginTop: "0px" }}
              >
                <Typography marginBottom="8px" fontWeight={700}>
                  Question:
                </Typography>
                <RichEditor
                  value={questionDescription}
                  onTextChanged={setQuestionDescription}
                  placeholder="Enter question (e.g) what is the area of a cirle of radius 12 metres using 3.14159 as pie"
                />
              </TextFieldContainer>
              <div style={{ display: "flex", justifyContent: "start" }}>
                <Typography
                  marginBottom="-16px"
                  marginTop="16px"
                  fontWeight={700}
                >
                  Options:
                </Typography>
              </div>
              {options.map((option, index) => {
                return (
                  <TextFieldContainer
                    key={option.uid}
                    direction="row"
                    style={{ alignItems: "center", position: "relative" }}
                  >
                    <ReactQuill
                      value={option.description}
                      placeholder="Enter option text"
                      className="quill-option"
                      onChange={(event) => updateOption(option.uid, event)}
                      modules={{
                        toolbar: [
                          [
                            "bold",
                            "italic",
                            "underline",
                            "strike",
                            "blockquote",
                          ],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ script: "sub" }, { script: "super" }],
                          ["formula"],
                        ],
                      }}
                    />
                    <Checkbox
                      name="selected-option-id"
                      icon={<FilledCircleIcon />}
                      checkedIcon={<CircleCheckedFilled />}
                      value={option.uid}
                      checked={option.uid === selectedOptionId}
                      onChange={(event) => {
                        setSelectedOptionId(event.target.value);
                      }}
                      style={{position: "absolute", right: "-20px", top: "-20px"}}
                    />
                    {index >= 2 && (
                      <IconButton
                        color="error"
                        onClick={() => deleteOption(option.uid)}
                        sx={{ position: "absolute", right: "-20px", bottom: "-20px" }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TextFieldContainer>
                );
              })}

              <TextFieldContainer
                direction="column"
                style={{ alignItems: "baseline", marginTop: "16px" }}
              >
                <Typography marginBottom="8px" fontWeight={700}>
                  Score:
                </Typography>
                <TextField
                  type="number"
                  name="points"
                  error={!!(errors.points && touched.points)}
                  helperText={errors.points}
                  onChange={(event) => {
                    console.log({ value: event.target.value });
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  value={values.points}
                  label="Score"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        style={{ marginRight: "-14px" }}
                      >
                        <Button
                          color="primary"
                          style={{
                            height: "54px",
                            maxHeight: "auto",
                            borderRadius: "0px",
                            fontWeight: 700,
                            minWidth: "80px",
                          }}
                        >
                          POINT
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </TextFieldContainer>

              {formErrorMessage && (
                <ErrorContainer>{formErrorMessage}</ErrorContainer>
              )}
            </div>

            <Footer>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={() => addToOptions()}
                disabled={isSubmitting}
              >
                ADD OPTION
              </Button>
              <Button
                disabled={isSubmitting}
                type="submit"
                variant="contained"
                color="primary"
              >
                {inEditMode ? "UPDATE QUESTION" : "ADD QUESTION"}
              </Button>
            </Footer>
          </Form>
        );
      }}
    </Formik>
  );
};

/**
 * ---------------------
 * STYLED COMPONENTS
 * ---------------------
 */

const TextFieldContainer = styled.div<{ direction?: "row" | "column" }>`
  margin-top: 24px;
  display: flex;
  flex-direction: ${(props) => props.direction || "column"};
`;

const Footer = styled.div`
  display: flex;
  border-top: dashed 2px #efefee;
  flex-direction: column;
  gap: 12px;
  @media (min-width: 500px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  margin-top: 32px;
  padding: 16px;
`;

const ErrorContainer = styled.div`
  text-align: left;
  margin-top: 16px;
  color: ${error};
`;

export default AddQuestionForm;
